import { Dropdown, Menu } from "antd";
import { AZURE_SENTIMENTS } from "@dots-projects/tasks";
import { useMutation, useQueryClient } from "react-query";
import { getSupaClient } from "../../lib/supabase";
import { DiscordSavedMessageTableData } from "./useDiscordMessages";

const useUpdateSentiment = () => {
  const supabase = getSupaClient();
  const guildId = localStorage.getItem("guild-id") || "";
  const queryClient = useQueryClient();

  const updateData = async ({
    id,
    newSentiment,
  }: {
    id: string;
    newSentiment: string;
  }) => {
    const { data, error } = await supabase
      .from("discord_saved_messages")
      .update({ sentiment: newSentiment })
      .match({ discord_id: id });

    if (data && data.length >= 0 && Array.isArray(data)) {
      return data;
    } else if (error) {
      throw new Error(error.message);
    } else {
      throw new Error(`Failed to update message sentiment: ${guildId}`);
    }
  };

  // Sentiment optimistic update
  return useMutation(updateData, {
    // When mutate is called:
    onMutate: async ({ id, newSentiment }) => {
      await queryClient.cancelQueries(["guildfetchAllSavedMessages", guildId]);
      const previousMessagesData = queryClient.getQueryData<
        DiscordSavedMessageTableData[]
      >(["guildfetchAllSavedMessages", guildId]);

      if (previousMessagesData) {
        queryClient.setQueryData<DiscordSavedMessageTableData[]>(
          ["guildfetchAllSavedMessages", guildId],
          previousMessagesData.map((msg) => {
            if (msg.discord_id === id) {
              return { ...msg, sentiment: newSentiment };
            }
            return msg;
          })
        );
      }

      return { previousMessagesData };
    },
    // If the mutation fails, use the context returned from onMutate to roll back
    onError: (_error, _variables, context) => {
      queryClient.setQueryData(
        ["guildfetchAllSavedMessages", guildId],
        context?.previousMessagesData
      );
    },
    // Always refetch after error or success:
    onSettled: () => {
      queryClient.invalidateQueries(["guildfetchAllSavedMessages", guildId]);
    },
  });
};

type Props = {
  message: DiscordSavedMessageTableData;
};

export default function MessageSentimentSelector({ message }: Props) {
  const sentiment = message.sentiment as AZURE_SENTIMENTS;

  const { mutate: updateSentiment } = useUpdateSentiment();

  const sentimentStyles = {
    mixed: "bg-purple-400 text-white",
    negative: "bg-red-400 text-white",
    neutral: "bg-gray-100 font-bold",
    positive: "bg-green-400 text-white",
  };

  const menu = (
    <Menu
      onClick={(selection) => {
        updateSentiment({
          id: message.discord_id,
          newSentiment: selection.key,
        });
      }}
    >
      <Menu.Item key="mixed">Mixed</Menu.Item>
      <Menu.Item key="negative">Negative</Menu.Item>
      <Menu.Item key="neutral">Neutral</Menu.Item>
      <Menu.Item key="positive">Positive</Menu.Item>
    </Menu>
  );

  return (
    <div className="flex justify-center items-center w-full">
      {sentiment ? (
        <Dropdown overlay={menu} placement="bottomLeft">
          <p
            className={`m-0 rounded-md px-2 py-1 font-bold ${sentimentStyles[sentiment]} text-center cursor-pointer`}
            style={{ minWidth: "80px" }}
          >
            {sentiment}
          </p>
        </Dropdown>
      ) : (
        ""
      )}
    </div>
  );
}
